/**
 * Modern Authentication Context
 * Updated to use the new API client with Supabase integration
 */

'use client';

import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from 'react';
import { useRouter } from 'next/navigation';
import { App } from 'antd';
import { appApi } from '@/infrastructure/api';
import { useAuthApi } from '@/domains/auth/hooks/use-auth-api';
import { validateUserAccess, authorizeOperation, getUserSecurityContext } from '@/domains/auth/services/secure-auth-service';
import { UserRole, UserPermissions } from '@/domains/auth/types/security-clearance';
import { useAppDispatch, useAppSelector } from '@/shared/providers/store';
import { selectAuth, clearAuthTokens } from '@/shared/providers/state/authSlice';
import {
  markSigningOut,
  clearClientAuthStorage,
  postLogoutApi,
  hardNavigateToLogin,
  isSigningOut,
  forceClientSessionExpiredSignOut,
} from '@/domains/auth/utils/sign-out';
import {
  validateToken,
  isTokenExpired,
  getStoredToken,
  getStoredUser,
  storeToken,
  clearStoredToken,
  getTimeUntilStoredTokenExpiry,
  TOKEN_KEYS,
  DEFAULT_EXPIRATION_BUFFER_MS,
  storeMfaUserId,
  getMfaUserId,
  clearMfaUserId,
  getActiveToken
} from '@/domains/auth/utils/token-utils';
import {
  storeTokenWithMetadata,
  clearAllTokenStorage,
  TokenMetadata
} from '@/domains/auth/utils/token-storage';
import type {
  User,
  LoginRequest,
  RegisterRequest,
  LoginResponse,
  MFAVerifyRequest
} from '@/infrastructure/api';

// Legacy token storage keys (keeping for migration compatibility)
const TOKEN_STORAGE_KEY = TOKEN_KEYS.ACCESS_TOKEN;
const REFRESH_TOKEN_STORAGE_KEY = TOKEN_KEYS.REFRESH_TOKEN;
const USER_STORAGE_KEY = TOKEN_KEYS.USER;

interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  isInitialized: boolean;
  requiresMFA: boolean;
  mfaUserId?: string;
  mfaMethods?: string[];
}

interface AuthContextType extends AuthState {
  // Authentication actions
  login: (credentials: LoginRequest) => Promise<{
    success: boolean;
    requiresMFA?: boolean;
    mfaUserId?: string;
    mfaMethods?: string[];
    error?: string;
  }>;
  register: (userData: RegisterRequest) => Promise<{
    success: boolean;
    user?: User;
    error?: string;
  }>;
  logout: () => Promise<void>;
  /** True while sign-out is in flight (show AuthPending, block re-verify). */
  isSigningOut: boolean;

  // MFA actions
  verifyMFA: (mfaData: MFAVerifyRequest) => Promise<{
    success: boolean;
    user?: User;
    error?: string;
  }>;
  setupMFA: () => Promise<{
    success: boolean;
    data?: unknown;
    error?: string;
  }>;
  verifyMFASetup: (token: string) => Promise<{
    success: boolean;
    error?: string;
  }>;
  verifyMFACredential: (credential: any) => Promise<{
    success: boolean;
    error?: string;
  }>;
  disableMFA: (token: string) => Promise<{
    success: boolean;
    error?: string;
  }>;

  // Token management
  refreshToken: () => Promise<boolean>;
  checkTokenExpiration: () => Promise<boolean>;
  isTokenExpired: () => boolean;

  // User profile
  updateProfile: () => Promise<void>;

  // Password management
  changePassword: (currentPassword: string, newPassword: string) => Promise<{
    success: boolean;
    error?: string;
  }>;

  // Password recovery
  requestPasswordReset: (email: string) => Promise<{
    success: boolean;
    error?: string;
  }>;

  // Utility functions
  clearError: () => void;
  checkAuthStatus: () => Promise<boolean>;

  // Security validation functions
  validateAccess: (resource?: string) => Promise<{
    isValid: boolean;
    role?: UserRole;
    permissions?: UserPermissions;
    error?: string;
  }>;
  authorizeUserOperation: (operation: string, resourceId?: string) => Promise<{
    authorized: boolean;
    reason?: string;
  }>;
  getSecurityContext: () => Promise<{
    role?: string;
    permissions?: any;
    features?: string[];
    error?: string;
  }>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  const reduxAuth = useAppSelector(selectAuth);
  if (!context) {
    throw new Error('useAuth must be used within a AuthProvider');
  }
  // While signing out (TTL-gated), never re-surface Redux as authenticated
  if (isSigningOut()) {
    return {
      ...context,
      user: null,
      isAuthenticated: false,
      isLoading: false,
      isInitialized: true,
      requiresMFA: false,
      isSigningOut: true,
    };
  }

  const shouldOverride =
    reduxAuth.isAuthenticated || reduxAuth.requiresMfa || reduxAuth.isLoading;

  if (!shouldOverride) {
    return context;
  }

  return {
    ...context,
    user: reduxAuth.user ?? context.user,
    isAuthenticated: reduxAuth.isAuthenticated,
    isLoading: reduxAuth.isLoading,
    isInitialized: context.isInitialized || reduxAuth.isAuthenticated || reduxAuth.requiresMfa,
    requiresMFA: reduxAuth.requiresMfa,
    mfaUserId: reduxAuth.mfaUserId || undefined,
    mfaMethods: reduxAuth.mfaMethods,
    isSigningOut: false,
  };
};

interface AuthProviderProps {
  children: React.ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const router = useRouter();
  const authApi = useAuthApi();
  const dispatch = useAppDispatch();
  const reduxAuth = useAppSelector(selectAuth);

  // Authentication state
  const [authState, setAuthState] = useState<AuthState>({
    user: null,
    isAuthenticated: false,
    isLoading: true,
    isInitialized: false,
    requiresMFA: false,
  });
  const [signingOut, setSigningOut] = useState(false);

  // Refs for cleanup
  const refreshIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const initializationRef = useRef(false);

  // Refs for function references to avoid circular dependencies
  const refreshTokenRef = useRef<(() => Promise<boolean>) | null>(null);
  const logoutRef = useRef<(() => Promise<void>) | null>(null);

  // Token expiration checking utilities
  const isTokenExpiredCheck = useCallback((): boolean => {
    const token = getStoredToken(TOKEN_KEYS.ACCESS_TOKEN);
    if (!token) return true;

    return isTokenExpired(token, DEFAULT_EXPIRATION_BUFFER_MS);
  }, []);

  const checkTokenExpiration = useCallback(async (): Promise<boolean> => {
    const token = getStoredToken(TOKEN_KEYS.ACCESS_TOKEN);
    if (!token) {
      console.log('AuthProvider: No token found during expiration check');
      return false;
    }

    const validation = validateToken(token, DEFAULT_EXPIRATION_BUFFER_MS);

    if (validation.isExpired) {
      console.log('AuthProvider: Token has expired, attempting refresh...');
      const refreshSuccess = await refreshToken();
      if (!refreshSuccess) {
        console.log('AuthProvider: Token refresh failed, clearing auth data');
        clearStoredToken(TOKEN_KEYS.ACCESS_TOKEN);
        clearStoredToken(TOKEN_KEYS.PROVISIONAL_TOKEN);
        clearStoredToken(TOKEN_KEYS.REFRESH_TOKEN);
        clearMfaUserId();
        localStorage.removeItem(USER_STORAGE_KEY);
        setAuthState(prev => ({
          ...prev,
          user: null,
          isAuthenticated: false,
          isLoading: false,
          requiresMFA: false,
        }));
        return false;
      }
      return true;
    } else if (validation.isExpiringSoon) {
      console.log(`AuthProvider: Token expires in ${validation.secondsUntilExpiration}s, refreshing proactively...`);
      // Proactively refresh token if it expires soon
      await refreshToken();
      return true;
    }

    return true;
  }, []);

  // Initialize authentication state


  // Token refresh interval with smart timing based on expiration
  const startTokenRefreshInterval = useCallback(() => {
    // Clear existing interval
    if (refreshIntervalRef.current) {
      clearInterval(refreshIntervalRef.current);
    }

    // Calculate optimal refresh interval based on token expiration
    const timeUntilExpiry = getTimeUntilStoredTokenExpiry();
    let refreshInterval: number;

    if (timeUntilExpiry > 0) {
      // Refresh at 75% of token lifetime, but at least every 25 minutes
      const optimalInterval = Math.max(timeUntilExpiry * 0.75, 25 * 60 * 1000);
      refreshInterval = Math.min(optimalInterval, 25 * 60 * 1000);
      console.log(`AuthProvider: Setting refresh interval to ${Math.floor(refreshInterval / 60000)} minutes based on token expiry`);
    } else {
      // Default to 25 minutes if we can't determine expiration
      refreshInterval = 25 * 60 * 1000;
      console.log('AuthProvider: Using default 25-minute refresh interval');
    }

    // Set up new interval
    refreshIntervalRef.current = setInterval(async () => {
      // Use refs to avoid circular dependencies
      const currentRefreshToken = refreshTokenRef.current;
      const currentLogout = logoutRef.current;

      if (currentRefreshToken && currentLogout) {
        // Check if token is expired before attempting refresh
        const token = getStoredToken(TOKEN_KEYS.ACCESS_TOKEN);
        if (token && isTokenExpired(token, DEFAULT_EXPIRATION_BUFFER_MS)) {
          console.log('AuthProvider: Token expired during interval check, refreshing...');
        }

        const success = await currentRefreshToken();
        if (!success) {
          console.log('AuthProvider: Token refresh failed during interval, logging out...');
          await currentLogout();
        }
      }
    }, refreshInterval);
  }, []);

  // Authentication actions
  const login = useCallback(async (credentials: LoginRequest) => {
    try {
      const result = await authApi.login(credentials);

      if (result.success && result.data) {
        const loginResponse = result.data;

        // Check if this is a provisional token (MFA setup required) or full access token (MFA already verified)
        const isProvisionalToken = loginResponse.message &&
          (loginResponse.message.toLowerCase().includes('provisional') ||
            loginResponse.message.toLowerCase().includes('mfa setup required') ||
            loginResponse.message.toLowerCase().includes('mfa verification required'));

        if (loginResponse.requires_mfa && loginResponse.access_token && isProvisionalToken) {
          storeToken(loginResponse.access_token, TOKEN_KEYS.PROVISIONAL_TOKEN);

          const userId = loginResponse.user_id;
          if (userId) {
            storeMfaUserId(userId);
          }

          // MFA required - backend uses snake_case
          const mfaMethods = loginResponse.mfa_methods;

          setAuthState(prev => ({
            ...prev,
            requiresMFA: true,
            mfaUserId: userId,
            mfaMethods: mfaMethods,
            isLoading: false,
          }));

          console.log('🔐 AuthProvider: MFA state set, returning MFA required response');
          return {
            success: true,
            requiresMFA: true,
            mfaUserId: userId,
            mfaMethods: mfaMethods,
          };
        } else if (loginResponse.requires_mfa && loginResponse.access_token && !isProvisionalToken) {
          // MFA is required but already verified - this is a full access token
          console.log('🔐 AuthProvider: MFA required but already verified, processing full access token');

          // Process as full authentication since MFA is already complete
          let user = loginResponse.user;

          if (!user) {
            console.log('🔐 AuthProvider: No user data in response, fetching profile...');
            // Set token first so profile request can be authenticated
            appApi.setAuthToken(loginResponse.access_token);
            // Also store token in localStorage for persistence
            storeToken(loginResponse.access_token);

            const profileResult = await authApi.getProfile();
            // Profile fetched
            if (profileResult.success && profileResult.data) {
              user = profileResult.data;
              console.log('🔐 AuthProvider: User profile fetched successfully');
            }
          }

          if (user) {
            console.log('🔐 AuthProvider: Storing full authentication data for MFA-verified user');
            storeToken(loginResponse.access_token, TOKEN_KEYS.ACCESS_TOKEN);
            if (loginResponse.refresh_token) {
              storeToken(loginResponse.refresh_token, TOKEN_KEYS.REFRESH_TOKEN);
            }
            // LAUNCH GATE: no auth in browser storage
            appApi.setAuthToken(loginResponse.access_token);

            setAuthState(prev => ({
              ...prev,
              user: user!,
              isAuthenticated: true,
              isLoading: false,
              requiresMFA: false,
            }));

            startTokenRefreshInterval();
            console.log('🔐 AuthProvider: Full authentication complete for MFA-verified user');

            return { success: true };
          } else {
            console.log('🔐 AuthProvider: Failed to get user data for MFA-verified user');
          }
        } else if (loginResponse.access_token && loginResponse.refresh_token) {
          // This is the full login flow, not the provisional MFA flow.
          console.log('🔐 AuthProvider: Processing full access token');

          let user = loginResponse.user;

          if (!user) {
            console.log('🔐 AuthProvider: No user data in response, fetching profile...');
            // Set token first so profile request can be authenticated
            appApi.setAuthToken(loginResponse.access_token);
            // Also store token in localStorage for persistence
            storeToken(loginResponse.access_token);

            const profileResult = await authApi.getProfile();
            // Profile fetched
            if (profileResult.success && profileResult.data) {
              user = profileResult.data;
              console.log('🔐 AuthProvider: User profile fetched successfully');
            }
          }

          if (user) {
            console.log('🔐 AuthProvider: Storing full authentication data');
            storeToken(loginResponse.access_token, TOKEN_KEYS.ACCESS_TOKEN);
            storeToken(loginResponse.refresh_token, TOKEN_KEYS.REFRESH_TOKEN);
            // LAUNCH GATE: no auth in browser storage
            appApi.setAuthToken(loginResponse.access_token);

            setAuthState(prev => ({
              ...prev,
              user: user!,
              isAuthenticated: true,
              isLoading: false,
              requiresMFA: false,
            }));

            startTokenRefreshInterval();
            console.log('🔐 AuthProvider: Full authentication complete');

            return { success: true };
          } else {
            console.log('🔐 AuthProvider: Failed to get user data');
          }
        } else {
          console.log('🔐 AuthProvider: Login response was not successful or did not contain a valid token.');
        }
      } else {
        console.log('🔐 AuthProvider: Login API call failed:', result.error);
      }

      return {
        success: false,
        error: result.error || 'Login failed'
      };
    } catch (error) {
      console.error('🔐 AuthProvider: Login error:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Login failed'
      };
    }
  }, [authApi, startTokenRefreshInterval]);

  const register = useCallback(async (userData: RegisterRequest) => {
    try {
      const result = await authApi.register(userData);

      if (result.success && result.data) {
        return {
          success: true,
          user: result.data
        };
      }

      return {
        success: false,
        error: result.error || 'Registration failed'
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Registration failed'
      };
    }
  }, [authApi]);

  const setupMFA = useCallback(async () => {
    try {
      const result = await authApi.setupMFA();
      return result;
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'MFA setup failed'
      };
    }
  }, [authApi]);

  /** LAUNCH GATE: MFA is IdP-only. No app-side TOTP verification. */
  const verifyMFASetup = useCallback(async (_token: string) => {
    return {
      success: false,
      error: 'MFA is managed by your identity provider. Sign in at login.<domain> for MFA.',
    };
  }, []);

  /** LAUNCH GATE: MFA is IdP-only. No app-side credential verification. */
  const verifyMFACredential = useCallback(async (_credential: unknown) => {
    return {
      success: false,
      error: 'MFA is managed by your identity provider. Sign in at login.<domain> for MFA.',
    };
  }, []);

  const updateProfile = useCallback(async () => {
    try {
      const result = await authApi.getProfile();
      if (result.success && result.data) {
        setAuthState(prev => ({
          ...prev,
          user: result.data!,
        }));
        // LAUNCH GATE: no auth in browser storage
      }
    } catch (error) {
      console.error('Profile update error:', error);
    }
  }, [authApi]);

  const disableMFA = useCallback(async (token: string) => {
    try {
      const result = await authApi.disableMFA(token);

      if (result.success) {
        // Update user profile to reflect MFA disabled
        await updateProfile();
      }

      return result;
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'MFA disable failed'
      };
    }
  }, [authApi, updateProfile]);

  const verifyMFA = useCallback(async (mfaData: MFAVerifyRequest) => {
    try {
      const result = await authApi.verifyMFA(mfaData);

      if (result.success && result.data) {
        const { access_token, user, refresh_token } = result.data;

        if (access_token && user) {
          storeToken(access_token, TOKEN_KEYS.ACCESS_TOKEN);
          if (refresh_token) {
            storeToken(refresh_token, TOKEN_KEYS.REFRESH_TOKEN);
          }
          // LAUNCH GATE: no auth in browser storage
          appApi.setAuthToken(access_token);
          // Clear the provisional token and MFA user ID now that we have a full access token
          clearStoredToken(TOKEN_KEYS.PROVISIONAL_TOKEN);
          clearMfaUserId();

          setAuthState(prev => ({
            ...prev,
            user,
            isAuthenticated: true,
            isLoading: false,
            requiresMFA: false,
            mfaUserId: undefined,
            mfaMethods: undefined,
          }));

          startTokenRefreshInterval();

          return { success: true, user };
        }
      }

      return {
        success: false,
        error: result.error || 'MFA verification failed'
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'MFA verification failed'
      };
    }
  }, [authApi, startTokenRefreshInterval]);

  /**
   * Bank-speed sign-out (brokerage pattern):
   * 1) Mark signing-out with short TTL (overlay safety only)
   * 2) Clear local session state synchronously
   * 3) Fire-and-forget BFF logout (beacon / keepalive)
   * 4) Hard navigate immediately — never await IdP revoke or artificial delay
   */
  const logout = useCallback(async (opts?: { returnTo?: string | null; error?: string | null }): Promise<void> => {
    const returnTo =
      opts?.returnTo ??
      (typeof window !== 'undefined'
        ? `${window.location.pathname}${window.location.search || ''}`
        : null);

    // If a previous attempt is mid-flight, only force navigation (don't re-teardown forever)
    if (isSigningOut()) {
      hardNavigateToLogin('client', {
        returnTo: opts?.error ? returnTo : undefined,
        error: opts?.error ?? undefined,
      });
      return;
    }

    markSigningOut();
    setSigningOut(true);

    // Stop token refresh loop
    if (refreshIntervalRef.current) {
      clearInterval(refreshIntervalRef.current);
      refreshIntervalRef.current = null;
    }

    // Optimistic local teardown first — UI unauthenticates immediately
    try {
      clearStoredToken(TOKEN_KEYS.ACCESS_TOKEN);
      clearStoredToken(TOKEN_KEYS.PROVISIONAL_TOKEN);
      clearStoredToken(TOKEN_KEYS.REFRESH_TOKEN);
      clearMfaUserId();
      try {
        clearAllTokenStorage();
      } catch {
        /* optional helper */
      }
      clearClientAuthStorage();
      appApi.clearAuthToken();
      dispatch(clearAuthTokens());

      setAuthState({
        user: null,
        isAuthenticated: false,
        isLoading: false,
        isInitialized: true,
        requiresMFA: false,
        mfaUserId: undefined,
        mfaMethods: undefined,
      });
    } catch (e) {
      console.warn('Local logout cleanup error:', e);
    }

    // Server cookie clear via beacon/keepalive — must not block leave
    void postLogoutApi(400);
    // Immediate hard leave (bank / brokerage). Overlay paints at most one frame.
    hardNavigateToLogin('client', {
      returnTo: opts?.error ? returnTo : undefined,
      error: opts?.error ?? undefined,
    });
  }, [dispatch]);

  // Store logout function in ref
  logoutRef.current = logout;

  const refreshToken = useCallback(async (): Promise<boolean> => {
    try {
      console.log('AuthProvider: Starting token refresh...');
      const storedRefreshToken = getStoredToken(TOKEN_KEYS.REFRESH_TOKEN);
      if (!storedRefreshToken) {
        console.log('AuthProvider: No stored refresh token found');
        return false;
      }

      const result = await authApi.refreshToken(storedRefreshToken);

      if (result.success && result.data) {
        const { access_token, refresh_token } = result.data;

        // Get current user with new token
        if (access_token) {
          const profileResult = await authApi.getProfile();

          if (profileResult.success && profileResult.data) {
            storeToken(access_token, TOKEN_KEYS.ACCESS_TOKEN);
            if (refresh_token) {
              storeToken(refresh_token, TOKEN_KEYS.REFRESH_TOKEN);
            }
            // LAUNCH GATE: no auth in browser storage
            appApi.setAuthToken(access_token);
            console.log('AuthProvider: Auth data stored successfully');

            setAuthState(prev => ({
              ...prev,
              user: profileResult.data!,
              isAuthenticated: true,
            }));

            return true;
          } else {
            console.log('AuthProvider: Failed to get profile after token refresh');
          }
        }
      } else {
        console.log('AuthProvider: Token refresh failed:', result.error);
      }

      return false;
    } catch (error) {
      console.error('AuthProvider: Token refresh error:', error);
      return false;
    }
  }, [authApi]);

  // Store refreshToken function in ref
  refreshTokenRef.current = refreshToken;

  const changePassword = useCallback(async (currentPassword: string, newPassword: string) => {
    try {
      const result = await authApi.changePassword({
        current_password: currentPassword,
        new_password: newPassword,
      });

      return {
        success: result.success,
        error: result.error,
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Password change failed',
      };
    }
  }, [authApi]);

  const requestPasswordReset = useCallback(async (email: string) => {
    try {
      const result = await authApi.requestPasswordReset(email);

      return {
        success: result.success,
        error: result.error,
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Password reset request failed',
      };
    }
  }, [authApi]);

  const checkAuthStatus = useCallback(async (): Promise<boolean> => {
    try {
      const result = await authApi.getProfile();
      return result.success;
    } catch {
      return false;
    }
  }, [authApi]);

  // Handle session expiry
  const { notification } = App.useApp();

  const handleSessionExpiry = useCallback((event: CustomEvent) => {
    const message =
      (event?.detail && typeof event.detail.message === 'string' && event.detail.message) ||
      'Your session has expired. Please sign in again.';

    // Avoid stacking toasts during bank-speed leave
    try {
      notification.warning({
        message: 'Session Expired',
        description: message,
        duration: 4,
        placement: 'topRight',
        key: 'session-expired',
      });
    } catch {
      /* App context may be unmounting */
    }

    // Check if we're in a provisional MFA flow (without decoding token)
    const provisionalToken = getStoredToken(TOKEN_KEYS.PROVISIONAL_TOKEN);
    const mfaUserId = getMfaUserId();

    if (provisionalToken && mfaUserId) {
      console.log('AuthProvider: Session expiry during provisional auth – keeping MFA flow active');
      setAuthState(prev => ({
        ...prev,
        isAuthenticated: false,
        requiresMFA: true,
        mfaUserId: mfaUserId,
      }));
      // Redirect to provisional MFA setup if not already on an auth route
      if (typeof window !== 'undefined' && !window.location.pathname.startsWith('/auth')) {
        router.push('/auth/mfa/provisional-setup');
      }
      return;
    }

    // Cookie + token sessions: bank-speed auto sign-out with return_to
    const returnTo =
      typeof window !== 'undefined'
        ? `${window.location.pathname}${window.location.search || ''}`
        : null;
    forceClientSessionExpiredSignOut({
      returnTo,
      error: 'session_expired',
    });
    // Keep React state coherent if hard nav is delayed a frame
    void logout({ returnTo, error: 'session_expired' });
  }, [logout, notification, router]);

  // Single useEffect for all auth initialization and management
  useEffect(() => {
    if (initializationRef.current) return;
    initializationRef.current = true;

    console.log('AuthProvider: Initializing authentication...');

    // If Redux already authenticated (e.g., login flow using cookies), mirror that into domain state
    if (reduxAuth.isAuthenticated) {
      const token = reduxAuth.sessionToken || reduxAuth.accessToken;
      if (token) {
        appApi.setAuthToken(token);
      }
      setAuthState(prev => ({
        ...prev,
        user: reduxAuth.user ?? prev.user,
        isAuthenticated: true,
        isLoading: false,
        isInitialized: true,
        requiresMFA: reduxAuth.requiresMfa,
        mfaUserId: reduxAuth.mfaUserId ?? undefined,
        mfaMethods: reduxAuth.mfaMethods ?? undefined,
      }));
      return;
    }

    // Initialize auth state
    const initAuth = async () => {
      try {
        console.log('🔐 AuthProvider: Starting authentication initialization...');
        setAuthState(prev => ({ ...prev, isLoading: true }));

        const storedToken = getStoredToken(TOKEN_KEYS.ACCESS_TOKEN);
        const storedUser = getStoredUser();

        console.log('🔐 AuthProvider: Stored token exists:', !!storedToken);
        console.log('🔐 AuthProvider: Stored user exists:', !!storedUser);

        if (storedToken) {
          console.log('🔐 AuthProvider: Processing stored token...');

          // Check if we have a provisional token by checking for stored MFA user ID
          const provisionalToken = getStoredToken(TOKEN_KEYS.PROVISIONAL_TOKEN);
          const mfaUserId = getMfaUserId();
          const isProvisionalFlow = !!provisionalToken && !!mfaUserId;

          if (isProvisionalFlow) {

            // For provisional tokens, don't try to fetch profile or refresh
            // Just set the token in API client and mark as requiring MFA
            appApi.setAuthToken(provisionalToken);
            setAuthState(prev => ({
              ...prev,
              user: null,
              isAuthenticated: false,
              isLoading: false,
              isInitialized: true,
              requiresMFA: true,
              mfaUserId: mfaUserId,
              mfaMethods: undefined,
            }));
            console.log('🔐 AuthProvider: Provisional token state set successfully');
            return;
          }

          // For non-provisional tokens, check if token is expired before making any API calls
          const validation = validateToken(storedToken, DEFAULT_EXPIRATION_BUFFER_MS);
          // Validate stored token

          if (validation.isExpired) {
            console.log('🔐 AuthProvider: Token is expired, attempting refresh...');
            const refreshSuccess = await refreshToken();
            if (!refreshSuccess) {
              console.log('🔐 AuthProvider: Token refresh failed, clearing auth data');
              clearStoredToken(TOKEN_KEYS.ACCESS_TOKEN);
              clearStoredToken(TOKEN_KEYS.PROVISIONAL_TOKEN);
              clearStoredToken(TOKEN_KEYS.REFRESH_TOKEN);
              localStorage.removeItem(USER_STORAGE_KEY);
              setAuthState(prev => ({
                ...prev,
                user: null,
                isAuthenticated: false,
                isLoading: false,
                isInitialized: true,
                requiresMFA: false,
              }));
              return;
            }
            console.log('🔐 AuthProvider: Token refreshed successfully, continuing...');
          }

          // For non-provisional tokens, proceed with normal validation
          appApi.setAuthToken(storedToken);
          console.log('🔐 AuthProvider: Token set in API client');

          // Only try to fetch profile if we have stored user data (indicating full access token)
          if (storedUser) {
            console.log('🔐 AuthProvider: Stored user found, verifying token by fetching profile...');
            // Verify token is still valid by fetching current user
            const profileResult = await authApi.getProfile();
            console.log('🔐 AuthProvider: Profile result:', profileResult);

            if (profileResult.success && profileResult.data) {
              console.log('🔐 AuthProvider: Token is valid, setting authenticated state');
              setAuthState(prev => ({
                ...prev,
                user: profileResult.data!,
                isAuthenticated: true,
                isLoading: false,
                isInitialized: true,
                requiresMFA: false,
              }));

              // Update stored user data
              // LAUNCH GATE: no auth in browser storage

              // Start token refresh interval
              startTokenRefreshInterval();
              console.log('🔐 AuthProvider: Authentication complete, user authenticated');
            } else {
              console.log('🔐 AuthProvider: Token is invalid, attempting refresh...');
              // Token is invalid, try to refresh
              const refreshSuccess = await refreshToken();
              console.log('🔐 AuthProvider: Token refresh result:', refreshSuccess);
              if (!refreshSuccess) {
                console.log('🔐 AuthProvider: Token refresh failed, clearing auth data');
                // Refresh failed, clear auth data
                clearStoredToken(TOKEN_KEYS.ACCESS_TOKEN);
                clearStoredToken(TOKEN_KEYS.PROVISIONAL_TOKEN);
                clearStoredToken(TOKEN_KEYS.REFRESH_TOKEN);
                clearMfaUserId();
                localStorage.removeItem(USER_STORAGE_KEY);
                setAuthState(prev => ({
                  ...prev,
                  user: null,
                  isAuthenticated: false,
                  isLoading: false,
                  isInitialized: true,
                  requiresMFA: false,
                }));
              }
            }
          } else {
            console.log('🔐 AuthProvider: No stored user data, setting unauthenticated state');
            setAuthState(prev => ({
              ...prev,
              user: null,
              isAuthenticated: false,
              isLoading: false,
              isInitialized: true,
              requiresMFA: false,
            }));
          }
        } else {
          // No localStorage token — try BFF httpOnly cookie session (client portal)
          console.log('🔐 AuthProvider: No local token; probing cookie session via verify-client…');
          try {
            const res = await fetch('/api/auth/verify-client', {
              method: 'GET',
              credentials: 'include',
              cache: 'no-store',
              headers: { Accept: 'application/json' },
              signal: AbortSignal.timeout(8000),
            });
            if (res.ok) {
              const data = (await res.json()) as {
                success?: boolean;
                user?: User & { userType?: string; clientData?: unknown };
              };
              if (data.success && data.user) {
                try {
                  sessionStorage.setItem('userType', data.user.userType || 'client');
                } catch {
                  /* no-op */
                }
                setAuthState(prev => ({
                  ...prev,
                  user: data.user as User,
                  isAuthenticated: true,
                  isLoading: false,
                  isInitialized: true,
                  requiresMFA: false,
                }));
                console.log('🔐 AuthProvider: Cookie session hydrated (client)');
                return;
              }
            }
          } catch (cookieErr) {
            console.log('🔐 AuthProvider: Cookie session probe failed', cookieErr);
          }

          setAuthState(prev => ({
            ...prev,
            user: null,
            isAuthenticated: false,
            isLoading: false,
            isInitialized: true,
            requiresMFA: false,
          }));
        }
      } catch (error) {
        console.error('🔐 AuthProvider: Auth initialization error:', error);
        clearStoredToken(TOKEN_KEYS.ACCESS_TOKEN);
        clearStoredToken(TOKEN_KEYS.PROVISIONAL_TOKEN);
        clearStoredToken(TOKEN_KEYS.REFRESH_TOKEN);
        clearMfaUserId();
        localStorage.removeItem(USER_STORAGE_KEY);
        setAuthState(prev => ({
          ...prev,
          user: null,
          isAuthenticated: false,
          isLoading: false,
          isInitialized: true,
          requiresMFA: false,
        }));
      }
    };

    // Set up event listeners for session management
    const sessionExpiryHandler = (event: Event) => {
      console.log('AuthProvider: Session expiry event received:', event);
      handleSessionExpiry(event as CustomEvent);
    };

    const tokenExpiringSoonHandler = async (event: Event) => {
      const customEvent = event as CustomEvent;
      console.log('AuthProvider: Token expiring soon event received:', customEvent.detail);

      // Proactively refresh the token
      try {
        const refreshSuccess = await refreshToken();
        if (refreshSuccess) {
          console.log('AuthProvider: Proactive token refresh successful');
        } else {
          console.warn('AuthProvider: Proactive token refresh failed');
        }
      } catch (error) {
        console.error('AuthProvider: Error during proactive token refresh:', error);
      }
    };

    // Initialize auth and set up event listeners
    const initFailsafe = window.setTimeout(() => {
      try {
        sessionStorage.setItem('mechagora:skip-auth-pending', '1')
      } catch {
        /* no-op */
      }
      setAuthState((prev) => {
        if (!prev.isLoading) return prev
        console.warn('AuthProvider: init exceeded 8s — clearing loading so the desk can paint')
        return { ...prev, isLoading: false, isInitialized: true }
      })
    }, 8_000)
    initAuth()
      .catch((err) => {
        console.error('AuthProvider: initAuth rejected', err)
      })
      .finally(() => {
        window.clearTimeout(initFailsafe)
        setAuthState((prev) =>
          prev.isLoading ? { ...prev, isLoading: false, isInitialized: true } : prev
        )
      })
      .then(() => {
        window.addEventListener('session-expired', sessionExpiryHandler)
        window.addEventListener('token-expiring-soon', tokenExpiringSoonHandler)
      })

    // Cleanup on unmount only
    return () => {
      console.log('AuthProvider: Cleaning up authentication...');
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current);
      }
      window.removeEventListener('session-expired', sessionExpiryHandler);
      window.removeEventListener('token-expiring-soon', tokenExpiringSoonHandler);
    };
  }, []); // Empty dependency array - only run once on mount

  /**
   * Cookie-session expiry watch (launch gate):
   * - Schedule sign-out from verify-client `expiresAt` / `exp` (JWT claim)
   * - Heartbeat re-verify so server-side invalidation also signs out
   */
  useEffect(() => {
    if (typeof window === 'undefined') return
    if (!authState.isInitialized || !authState.isAuthenticated) return
    if (isSigningOut()) return

    let cancelled = false
    let expTimer: number | null = null
    let heartbeatTimer: number | null = null

    const fireExpired = (reason: string) => {
      if (cancelled || isSigningOut()) return
      window.dispatchEvent(
        new CustomEvent('session-expired', {
          detail: {
            message: reason,
            timestamp: new Date().toISOString(),
          },
        })
      )
    }

    const scheduleFromExp = (expSec: number | null | undefined) => {
      if (expTimer != null) {
        window.clearTimeout(expTimer)
        expTimer = null
      }
      if (!expSec || !Number.isFinite(expSec)) return
      // Sign out 30s before JWT exp (or immediately if already past buffer)
      const msUntil = expSec * 1000 - Date.now() - 30_000
      const delay = Math.max(0, Math.min(msUntil, 24 * 60 * 60 * 1000))
      expTimer = window.setTimeout(() => {
        fireExpired('Your session has expired. Please sign in again.')
      }, delay)
    }

    const checkSession = async () => {
      if (cancelled || isSigningOut()) return
      try {
        const res = await fetch('/api/auth/verify-client', {
          method: 'GET',
          credentials: 'include',
          cache: 'no-store',
          headers: { 'Content-Type': 'application/json' },
        })
        if (cancelled) return
        if (res.status === 401 || res.status === 403) {
          fireExpired('Your session has expired. Please sign in again.')
          return
        }
        if (!res.ok) return
        const data = (await res.json().catch(() => null)) as {
          success?: boolean
          exp?: number | null
          expiresAt?: string | null
        } | null
        if (!data?.success) {
          fireExpired('Your session has expired. Please sign in again.')
          return
        }
        const exp =
          typeof data.exp === 'number'
            ? data.exp
            : data.expiresAt
              ? Math.floor(new Date(data.expiresAt).getTime() / 1000)
              : null
        scheduleFromExp(exp)
      } catch {
        /* network blip — do not sign out; next heartbeat retries */
      }
    }

    void checkSession()
    // Heartbeat every 60s (desk stays honest if cookie revoked server-side)
    heartbeatTimer = window.setInterval(() => {
      void checkSession()
    }, 60_000)

    // Re-check when tab becomes visible again
    const onVisible = () => {
      if (document.visibilityState === 'visible') void checkSession()
    }
    document.addEventListener('visibilitychange', onVisible)

    return () => {
      cancelled = true
      if (expTimer != null) window.clearTimeout(expTimer)
      if (heartbeatTimer != null) window.clearInterval(heartbeatTimer)
      document.removeEventListener('visibilitychange', onVisible)
    }
  }, [authState.isInitialized, authState.isAuthenticated])

  // Separate effect for MFA redirects (only when auth state changes)
  // Auto-redirect to MFA setup if required (only after proper login flow)
  useEffect(() => {
    if (!authState.isInitialized) return;

    // Only redirect to MFA setup if:
    // 1. User is authenticated OR we have a valid provisional token
    // 2. MFA is actually required
    // 3. We're not already on an auth route
    // 4. We have both provisional token and MFA user ID (indicating proper login flow)
    const hasValidProvisionalState = !!getStoredToken(TOKEN_KEYS.PROVISIONAL_TOKEN) && !!getMfaUserId();

    if (authState.requiresMFA && hasValidProvisionalState) {
      console.log('AuthProvider: Valid MFA requirement detected, redirecting to provisional MFA setup');

      // Only redirect if we're not already on an auth route
      if (!window.location.pathname.startsWith('/auth')) {
        router.push('/auth/mfa/provisional-setup');
      }
    } else if (authState.requiresMFA && !hasValidProvisionalState) {
      // Clear invalid MFA state that might be leftover from previous sessions
      console.log('AuthProvider: Clearing invalid MFA state (no valid provisional token found)');
      setAuthState(prev => ({
        ...prev,
        requiresMFA: false,
        mfaUserId: undefined,
        mfaMethods: undefined,
      }));
      clearStoredToken(TOKEN_KEYS.PROVISIONAL_TOKEN);
      clearMfaUserId();
    }
  }, [authState.requiresMFA, authState.mfaUserId, authState.isInitialized, router]);

  // Keep domain auth state aligned with Redux auth after initialization
  useEffect(() => {
    if (reduxAuth.isAuthenticated) {
      const token = reduxAuth.sessionToken || reduxAuth.accessToken;
      if (token) {
        appApi.setAuthToken(token);
      }
      setAuthState(prev => ({
        ...prev,
        user: reduxAuth.user ?? prev.user,
        isAuthenticated: true,
        isInitialized: true,
        isLoading: false,
        requiresMFA: reduxAuth.requiresMfa,
        mfaUserId: reduxAuth.mfaUserId ?? prev.mfaUserId,
        mfaMethods: reduxAuth.mfaMethods ?? prev.mfaMethods,
      }));
    }
  }, [
    reduxAuth.isAuthenticated,
    reduxAuth.user,
    reduxAuth.requiresMfa,
    reduxAuth.mfaUserId,
    reduxAuth.mfaMethods,
    reduxAuth.sessionToken,
    reduxAuth.accessToken,
  ]);

  // Security validation functions
  const validateAccess = useCallback(async (resource?: string) => {
    if (!authState.user?.id) {
      return { isValid: false, error: 'User not authenticated' };
    }

    try {
      const result = await validateUserAccess(authState.user.id, resource);
      return result;
    } catch (error) {
      console.error('Access validation error:', error);
      return { isValid: false, error: 'Access validation failed' };
    }
  }, [authState.user?.id]);

  const authorizeUserOperation = useCallback(async (operation: string, resourceId?: string) => {
    if (!authState.user?.id) {
      return { authorized: false, reason: 'User not authenticated' };
    }

    try {
      const result = await authorizeOperation(authState.user.id, operation, resourceId);
      return result;
    } catch (error) {
      console.error('Operation authorization error:', error);
      return { authorized: false, reason: 'Authorization failed' };
    }
  }, [authState.user?.id]);

  const getSecurityContext = useCallback(async () => {
    if (!authState.user?.id) {
      return { error: 'User not authenticated' };
    }

    try {
      const result = await getUserSecurityContext(authState.user.id);
      return result;
    } catch (error) {
      console.error('Security context error:', error);
      return { error: 'Security context retrieval failed' };
    }
  }, [authState.user?.id]);

  // isSigningOut is TTL-gated via sessionStorage — never permanent React state alone
  const contextValue: AuthContextType = {
    // State
    ...authState,
    isSigningOut: isSigningOut(),

    // Actions
    login,
    register,
    logout,
    verifyMFA,
    setupMFA,
    verifyMFASetup,
    verifyMFACredential,
    disableMFA,
    refreshToken,
    checkTokenExpiration,
    isTokenExpired: isTokenExpiredCheck,
    updateProfile,
    changePassword,
    requestPasswordReset,
    checkAuthStatus,
    clearError: authApi.clearError,

    // Security validation functions
    validateAccess,
    authorizeUserOperation,
    getSecurityContext,
  };

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};
